/**
 * Pure helpers for `<CellDetailPanel>` (in-platform-model-view Phase 4
 * Session 2). No React, no DOM — fully unit-testable.
 *
 * Four responsibilities:
 *   1. `findOutputDefinition(name, schema)` — narrow `schema.outputs[]`
 *      (typed `unknown[]` on the wire) to a `ResolvedOutputDef` or null.
 *   2. `findInputDefinition(name, schema)` — same for `schema.inputs[]`.
 *   3. `findNamedRange(name, schema)` — narrow `schema.allNamedRanges[]`
 *      to `{ name, sheet, cell }` for the click-upstream lookup. Mirrors
 *      `namedRangeInfoSchema` field layout.
 *   4. `buildDependencyTreeData(name, schema, cellValues, inputValues,
 *      activeSheet)` — BFS over `dependencyGraph` edges (filtered to
 *      `type === 'direct'` to avoid double-counting transitive paths) up
 *      to depth 3 each direction. Annotates each node with displayName +
 *      value + isInput / isOutput flags.
 *
 * Field-name discipline: `inputDefinitionSchema` and `outputDefinitionSchema`
 * carry `sheet: string` and `cell: string` as TWO SEPARATE fields (NOT a
 * single `cellAddress`). `namedRangeInfoSchema` exposes `{ name, displayName,
 * ref, sheet, cell, cellType, value?, formula? }`. Verified at
 * `packages/shared-types/src/lib/model.types.ts`.
 */
import type { ModelViewResponse } from '@/lib/api';

type SchemaSubset = ModelViewResponse['schema'];

export interface ResolvedOutputDef {
  name: string;
  displayName?: string;
  sheet?: string;
  cell?: string;
  dataType?: string;
  category?: string;
}

export interface ResolvedInputDef {
  name: string;
  displayName?: string;
  sheet?: string;
  cell?: string;
  dataType?: string;
  category?: string;
  priority?: string;
}

export interface ResolvedNamedRange {
  name: string;
  displayName?: string;
  sheet?: string;
  cell?: string;
}

export interface DependencyEdge {
  from: string;
  to: string;
  type?: string;
}

export interface DependencyNode {
  name: string;
  displayName: string;
  value: unknown;
  depth: number;
  isInput: boolean;
  isOutput: boolean;
}

export interface DependencyTreeData {
  name: string;
  displayName?: string;
  formula?: string;
  currentValue: unknown;
  upstream: DependencyNode[];
  downstream: DependencyNode[];
}

const MAX_DEPTH = 3;

function asObjectArray<T>(arr: unknown): T[] {
  return Array.isArray(arr) ? (arr as T[]) : [];
}

function readField(o: unknown, key: string): unknown {
  if (o === null || typeof o !== 'object') return undefined;
  return (o as Record<string, unknown>)[key];
}

function readString(o: unknown, key: string): string | undefined {
  const v = readField(o, key);
  return typeof v === 'string' ? v : undefined;
}

export function findOutputDefinition(
  name: string,
  schema: SchemaSubset
): ResolvedOutputDef | null {
  for (const raw of asObjectArray<unknown>(schema.outputs)) {
    if (readString(raw, 'name') !== name) continue;
    return {
      name,
      displayName: readString(raw, 'displayName'),
      sheet: readString(raw, 'sheet'),
      cell: readString(raw, 'cell'),
      dataType: readString(raw, 'dataType'),
      category: readString(raw, 'category'),
    };
  }
  return null;
}

export function findInputDefinition(
  name: string,
  schema: SchemaSubset
): ResolvedInputDef | null {
  for (const raw of asObjectArray<unknown>(schema.inputs)) {
    if (readString(raw, 'name') !== name) continue;
    return {
      name,
      displayName: readString(raw, 'displayName'),
      sheet: readString(raw, 'sheet'),
      cell: readString(raw, 'cell'),
      dataType: readString(raw, 'dataType'),
      category: readString(raw, 'category'),
      priority: readString(raw, 'priority'),
    };
  }
  // Fallback: table-input member lookup. Tables like UnitMix register each
  // cell with its own auto-injected namedRange (e.g. `UnitMix.Studio.Units`)
  // but the scalar `schema.inputs[]` list deliberately excludes those (see
  // `filterTableMemberInputs` in agents ingestion). Without this fallback,
  // every table-member input cell falls through CellDetailPanel's input
  // branch to the "Edit via chat" hint instead of rendering the inline editor.
  // Synthesize a ResolvedInputDef using the column's dataType + a row-aware
  // displayName so the editor (and downstream provenance + downstream graph)
  // can do its job. Verified shape against `tableInputDefinitionSchema` /
  // `tableRowDefSchema` / `tableColumnDefSchema`.
  for (const tableRaw of asObjectArray<unknown>(
    (schema as unknown as { tableInputs?: unknown }).tableInputs
  )) {
    const tableName = readString(tableRaw, 'name');
    const tableSheet = readString(tableRaw, 'sheet');
    const columns = asObjectArray<unknown>(readField(tableRaw, 'columns'));
    const rows = asObjectArray<unknown>(readField(tableRaw, 'rows'));
    for (const row of rows) {
      const cells = readField(row, 'cells');
      if (cells === null || typeof cells !== 'object') continue;
      const cellMap = cells as Record<string, unknown>;
      for (const [columnKey, namedRange] of Object.entries(cellMap)) {
        if (namedRange !== name) continue;
        const col = columns.find((c) => readString(c, 'key') === columnKey);
        const colCellAddr = readString(col, 'cell');
        const rowLabel = readString(row, 'label');
        const colHeader = readString(col, 'displayName');
        return {
          name,
          displayName:
            rowLabel && colHeader
              ? `${rowLabel} · ${colHeader}`
              : tableName
              ? `${tableName} · ${columnKey}`
              : name,
          sheet: tableSheet,
          cell: colCellAddr,
          dataType: readString(col, 'dataType'),
          category: tableName,
        };
      }
    }
  }
  return null;
}

export function findNamedRange(
  name: string,
  schema: SchemaSubset
): ResolvedNamedRange | null {
  for (const raw of asObjectArray<unknown>(schema.allNamedRanges)) {
    if (readString(raw, 'name') !== name) continue;
    return {
      name,
      displayName: readString(raw, 'displayName'),
      sheet: readString(raw, 'sheet'),
      cell: readString(raw, 'cell'),
    };
  }
  return null;
}

/**
 * Resolve a node's display value:
 *   - input → `inputValues[name]`
 *   - output / intermediate → `cellValues[${sheet}!${cell}]`
 *   - unknown → null
 */
function resolveNodeValue(
  name: string,
  schema: SchemaSubset,
  cellValues: Record<string, unknown>,
  inputValues: Record<string, unknown>
): {
  value: unknown;
  isInput: boolean;
  isOutput: boolean;
  displayName: string;
} {
  const input = findInputDefinition(name, schema);
  if (input) {
    return {
      value: inputValues[name] ?? null,
      isInput: true,
      isOutput: false,
      displayName: input.displayName ?? name,
    };
  }
  const output = findOutputDefinition(name, schema);
  if (output) {
    const key =
      output.sheet && output.cell ? `${output.sheet}!${output.cell}` : null;
    return {
      value: key !== null ? cellValues[key] ?? null : null,
      isInput: false,
      isOutput: true,
      displayName: output.displayName ?? name,
    };
  }
  // Fall back to namedRange (intermediate cells) — value lookup via
  // sheet+cell key, displayName from the named-range entry.
  const nr = findNamedRange(name, schema);
  if (nr) {
    const key = nr.sheet && nr.cell ? `${nr.sheet}!${nr.cell}` : null;
    return {
      value: key !== null ? cellValues[key] ?? null : null,
      isInput: false,
      isOutput: false,
      displayName: nr.displayName ?? name,
    };
  }
  return {
    value: null,
    isInput: false,
    isOutput: false,
    displayName: name,
  };
}

/**
 * BFS along `dependencyGraph` edges in one direction. Filters to direct
 * edges only — transitive edges are pre-computed reachability shortcuts
 * that double-count nodes already reachable through the direct chain.
 */
function bfsDirected(
  startName: string,
  edges: DependencyEdge[],
  direction: 'upstream' | 'downstream',
  schema: SchemaSubset,
  cellValues: Record<string, unknown>,
  inputValues: Record<string, unknown>
): DependencyNode[] {
  const visited = new Set<string>([startName]);
  const out: DependencyNode[] = [];
  let frontier: string[] = [startName];
  for (let depth = 1; depth <= MAX_DEPTH; depth += 1) {
    const next: string[] = [];
    for (const node of frontier) {
      for (const edge of edges) {
        if (edge.type !== undefined && edge.type !== 'direct') continue;
        const matches =
          direction === 'upstream' ? edge.to === node : edge.from === node;
        if (!matches) continue;
        const neighbor = direction === 'upstream' ? edge.from : edge.to;
        if (visited.has(neighbor)) continue;
        visited.add(neighbor);
        const resolved = resolveNodeValue(
          neighbor,
          schema,
          cellValues,
          inputValues
        );
        out.push({
          name: neighbor,
          displayName: resolved.displayName,
          value: resolved.value,
          depth,
          isInput: resolved.isInput,
          isOutput: resolved.isOutput,
        });
        next.push(neighbor);
      }
    }
    frontier = next;
    if (frontier.length === 0) break;
  }
  return out;
}

export function buildDependencyTreeData(
  name: string,
  schema: SchemaSubset,
  cellValues: Record<string, unknown>,
  inputValues: Record<string, unknown>,
  /** Active sheet name — fallback for cell-value lookup when the named range's sheet isn't on the schema. */
  fallbackSheet: string
): DependencyTreeData {
  const edges = asObjectArray<DependencyEdge>(schema.dependencyGraph);

  const self = resolveNodeValue(name, schema, cellValues, inputValues);
  // Resolve `currentValue` with a `${fallbackSheet}!${cell}` lookup if the
  // schema-level lookup missed (rare — covers cells that exist in the layout
  // but aren't in `outputs[]` like intermediate computed cells).
  let currentValue = self.value;
  if (currentValue === null) {
    const output = findOutputDefinition(name, schema);
    const sheet = output?.sheet ?? fallbackSheet;
    const cell = output?.cell;
    if (cell) {
      currentValue = cellValues[`${sheet}!${cell}`] ?? null;
    }
  }

  const upstream = bfsDirected(
    name,
    edges,
    'upstream',
    schema,
    cellValues,
    inputValues
  );
  const downstream = bfsDirected(
    name,
    edges,
    'downstream',
    schema,
    cellValues,
    inputValues
  );

  return {
    name,
    displayName: self.displayName !== name ? self.displayName : undefined,
    currentValue,
    upstream,
    downstream,
  };
}

/**
 * Count distinct upstream INPUT nodes for the "Derived (calculated from N
 * inputs)" provenance footer. Filters to `isInput=true` per spec §7.
 */
export function countUpstreamInputs(tree: DependencyTreeData): number {
  return tree.upstream.filter((n) => n.isInput).length;
}

/**
 * Format a provenance ISO timestamp for display in the cell detail panel.
 * Produces a relative-then-absolute hybrid: `"3 min ago"` for very-recent
 * edits (under 1 hour), then `"Apr 26, 14:32"` for everything older.
 *
 * Defensive — invalid input returns `"—"` so a malformed `provenance`
 * payload doesn't crash the panel.
 */
export function formatProvenanceTimestamp(iso: unknown): string {
  if (typeof iso !== 'string') return '—';
  const ts = Date.parse(iso);
  if (!Number.isFinite(ts)) return '—';
  const now = Date.now();
  const diffSec = Math.floor((now - ts) / 1000);
  if (diffSec < 0) {
    // Future timestamp — surface verbatim for debugging.
    return new Date(ts).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  }
  if (diffSec < 60) return 'just now';
  if (diffSec < 3600) {
    const m = Math.floor(diffSec / 60);
    return `${m} min ago`;
  }
  return new Date(ts).toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/**
 * Walk `dependencyGraph` downward from `name` (BFS, depth 3, direct edges
 * only) and return the named-range list — used for the input-cell branch's
 * "Affects" downstream-impact list. Per spec §3, returns NAMES ONLY (no
 * before/after pairs since `prevValue: null` for downstream entries on a
 * single-input edit). Each name is also display-name-resolved when found
 * in `outputs[]` / `intermediates[]` / `allNamedRanges[]`.
 */
export function getDownstreamNamedRanges(
  name: string,
  schema: SchemaSubset
): Array<{ name: string; displayName: string }> {
  const edges = asObjectArray<DependencyEdge>(schema.dependencyGraph);
  const visited = new Set<string>([name]);
  const out: Array<{ name: string; displayName: string }> = [];
  let frontier: string[] = [name];
  for (let depth = 1; depth <= MAX_DEPTH; depth += 1) {
    const next: string[] = [];
    for (const node of frontier) {
      for (const edge of edges) {
        if (edge.type !== undefined && edge.type !== 'direct') continue;
        if (edge.from !== node) continue;
        const neighbor = edge.to;
        if (visited.has(neighbor)) continue;
        visited.add(neighbor);
        const resolved = resolveNodeValue(neighbor, schema, {}, {});
        out.push({ name: neighbor, displayName: resolved.displayName });
        next.push(neighbor);
      }
    }
    frontier = next;
    if (frontier.length === 0) break;
  }
  return out;
}
